import { type HomeHealthAlertEnabledMap } from "../types";

type AlertSettings = Record<string, Record<string, unknown>>;
type AlertEntry = Record<string, unknown>;

/**
 * Builds the alertSettings blob to submit when saving Home Health preferences.
 *
 * Round-trip invariant: every key in `existing` must appear in the result —
 * only the EMAIL entries named in `edits` are mutated. This preserves keys the
 * current view doesn't surface (e.g. an LTC workplace's alerts on a multi-
 * workplace user) instead of silently dropping them.
 *
 * The "All" key in `edits` writes directly to EMAIL.All, which the backend
 * mirrors to Knock's channel-level mute (suppresses every workflow on EMAIL
 * for the user across every workplace).
 */
export function buildMergedAlertSettings(params: {
  existing: AlertSettings | undefined;
  edits: HomeHealthAlertEnabledMap;
}): AlertSettings {
  const { existing, edits } = params;

  const existingEmail = (existing?.EMAIL ?? {}) as Record<string, AlertEntry | undefined>;
  const updatedEmail: Record<string, AlertEntry | undefined> = { ...existingEmail };

  for (const [alertKey, enabled] of Object.entries(edits)) {
    updatedEmail[alertKey] = { ...existingEmail[alertKey], enabled };
  }

  return {
    ...existing,
    EMAIL: updatedEmail as Record<string, unknown>,
  };
}

/**
 * EMAIL-only by design — the Home Health view does not surface SMS. Knock and
 * the backend treat a missing entry as enabled, so we mirror that here. The
 * "All" key follows the same rule: missing or undefined means the channel is
 * not muted.
 */
export function getEmailRowEnabled(
  alertSettings: AlertSettings | undefined,
  alertKey: string
): boolean {
  const entry = alertSettings?.EMAIL?.[alertKey] as AlertEntry | undefined;
  return entry?.enabled !== false;
}
